import { useEffect, useRef, useState } from 'react'
import { Trash2, X, Loader2, ShieldAlert } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useConfirmarExclusao } from '@/lib/confirmarExclusao'

// FEATURE 02/07: modal ÚNICO de confirmação de exclusão (senha de gerente/admin/super).
// Montado uma vez no App. Lê o pedido do store (useConfirmarExclusao) e:
//   1) exige a senha de um admin/gerente/super_admin (POST /auth/autorizar-exclusao);
//   2) só após o 200 roda o onConfirm (o DELETE de verdade).
// Campo de senha NÃO pode ser preenchido nem salvo pelo navegador → usamos
// type=text + WebkitTextSecurity (mascara visual sem ser <input type=password>),
// autoComplete=off, name aleatório e os data-* que desligam LastPass/1Password.
export default function ModalConfirmarExclusao() {
  const pedido = useConfirmarExclusao(s => s.pedido)
  const fechar = useConfirmarExclusao(s => s.fechar)
  const [senha, setSenha] = useState('')
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // name aleatório por abertura: impede o navegador de casar com uma senha salva.
  const [fieldName] = useState(() => `excl-${Math.random().toString(36).slice(2)}`)

  // Ao abrir: limpa senha e foca o campo.
  useEffect(() => {
    if (pedido) {
      setSenha('')
      setLoading(false)
      const t = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [pedido])

  if (!pedido) return null

  const confirmar = async () => {
    const s = senha.trim()
    if (!s || loading) return
    setLoading(true)
    try {
      // 1) valida a senha (não apaga nada). 403 = senha/cargo inválido.
      await api.post('/auth/autorizar-exclusao', { senha: s })
    } catch (e: any) {
      setLoading(false)
      toast.error(e?.response?.data?.detail || 'Senha não autorizada para excluir')
      return
    }
    // 2) autorizado → executa a exclusão real.
    try {
      await pedido.onConfirm()
      fechar()
    } catch {
      // onConfirm (ex.: mutation) normalmente já mostra o próprio erro via toast.
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-ink-950/70 z-[100] flex items-center justify-center p-4"
      onClick={() => !loading && fechar()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
          <h3 className="font-bold text-ink-900 flex items-center gap-2">
            <ShieldAlert size={18} className="text-rose-600" /> {pedido.titulo || 'Confirmar exclusão'}
          </h3>
          <button onClick={() => !loading && fechar()} className="p-1.5 rounded hover:bg-ink-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-ink-700">
            {pedido.descricao
              ? <>Tem certeza que deseja excluir <strong>{pedido.descricao}</strong>? Esta ação não pode ser desfeita.</>
              : <>Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.</>}
          </div>
          <div>
            <label className="text-xs text-ink-600">Senha de autorização *</label>
            {/* Anti-autofill: type=text + text-security mascara SEM ser campo de senha,
                então o navegador/gerenciador não oferece nem salva a senha. */}
            <form autoComplete="off" onSubmit={e => { e.preventDefault(); confirmar() }}>
              {/* honeypot invisível: alguns gerenciadores insistem em preencher o 1º campo */}
              <input type="text" name="username" autoComplete="username" tabIndex={-1}
                aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
              <input
                ref={inputRef}
                type="text"
                name={fieldName}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore
                data-bwignore
                data-form-type="other"
                readOnly
                onFocus={e => e.currentTarget.removeAttribute('readonly')}
                className="input w-full mt-1 text-sm"
                style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' } as any}
                value={senha}
                onChange={e => setSenha(e.target.value)}
                placeholder="Senha de admin, gerente ou supervisor"
              />
            </form>
            <p className="text-[11px] text-ink-500 mt-1">
              Exclusão exige cargo. Se você não tem, use a senha de um admin, gerente ou supervisor.
            </p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-ink-200 flex justify-end gap-2">
          <button onClick={() => !loading && fechar()} className="btn-secondary text-sm">Cancelar</button>
          <button
            onClick={confirmar}
            disabled={loading || !senha.trim()}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} {pedido.confirmLabel || 'Excluir'}
          </button>
        </div>
      </div>
    </div>
  )
}
